import { useEffect, useMemo, useState } from "react";
import { ConfirmationPageBase } from "../ConfirmationPageBase";
import { useConfirmationPageData } from "../useConfirmationPageData";
import { PDFService } from "../../../services/PDFService";
import { DO_GOOGLE_REVIEW_REDIRECT_SECONDS } from "../../../config/timing";
import { getAzulResponseParams } from "../../../services/dtos/response/AzulResponseDto";

export default function ConfirmationPageDO() {
  const { employee, isLoading, error, isFromAzul, state } = useConfirmationPageData();
  const [downloading, setDownloading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const pdfService = useMemo(() => new PDFService(), []);
  const [googleRedirectUrl, setGoogleRedirectUrl] = useState<string | null>(null);
  const [googleSecondsLeft, setGoogleSecondsLeft] = useState<number | null>(null);

  // Mensajes específicos para Dominicana
  const title = isFromAzul ? "¡Pago Exitoso!" : "¡Gracias!";

  const message = isFromAzul
    ? "Gracias por tu propina. Tu valoración nos ayuda a mejorar."
    : "Tu valoración nos sirve para mejorar.";

  const minRating = 4;

  const handleDownloadPdf = async () => {
    if (downloading) return;

    const tipIdFromStorage = sessionStorage.getItem('azulTipId');
    
    if (!tipIdFromStorage) {
      setPdfError("No se encontró información de la transacción");
      return;
    }

    const tipId = parseInt(tipIdFromStorage, 10);
    if (isNaN(tipId)) {
      setPdfError("Error al procesar la información de la transacción");
      return;
    }

    setDownloading(true);
    setPdfError(null);
    try {
      await pdfService.getTipConfirmationPDF(
        { tipId },
        (data) => {
          const buffer = new Uint8Array(data.pdf.data);
          const blob = new Blob([buffer], { type: "application/pdf" });

          const blobUrl = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = "Comprobante_Divix.pdf";
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(blobUrl);
        },
        (errorMessage) => {
          setPdfError(errorMessage || "Error al descargar el comprobante. Por favor, inténtalo nuevamente.");
        }
      );
    } catch (err: any) {
      setPdfError(err?.message || "Error al descargar el comprobante. Por favor, inténtalo nuevamente.");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!employee) return;
    if (employee.countryIsoCode?.toUpperCase() !== "DO") return;

    const ratingRaw = isFromAzul
      ? sessionStorage.getItem("azulRating")
      : (state?.rating != null ? String(state.rating) : null);
    const googleLinkRaw = isFromAzul
      ? sessionStorage.getItem("azulGoogleLink")
      : (state?.googleLink ?? null);

    const rating = ratingRaw ? parseInt(ratingRaw, 10) : NaN;
    const googleLink = (googleLinkRaw ?? "").trim();

    if (!Number.isFinite(rating) || rating < minRating || googleLink.length === 0) {
      setGoogleRedirectUrl(null);
      return;
    }

    const normalizedUrl = googleLink.includes("https://") ? googleLink : `https://${googleLink}`;
    setGoogleRedirectUrl(normalizedUrl);
    setGoogleSecondsLeft(DO_GOOGLE_REVIEW_REDIRECT_SECONDS);

    let seconds = DO_GOOGLE_REVIEW_REDIRECT_SECONDS;
    const intervalId = window.setInterval(() => {
      seconds -= 1;
      setGoogleSecondsLeft(seconds);
      if (seconds <= 0) {
        window.clearInterval(intervalId);
        window.location.replace(normalizedUrl);
      }
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [employee, isFromAzul, state]);

  const getTipSummaryData = () => {
    if (!employee || !isFromAzul) return null;

    const tipIdFromStorage = sessionStorage.getItem('azulTipId');
    if (!tipIdFromStorage) return null;

    const azulResponse = getAzulResponseParams();
    // Azul devuelve el monto en centavos por lo que debemos parsearlo
    const amountRaw = azulResponse.Amount || "0";
    const amount = parseFloat(amountRaw) / 100;
    const azulOrderId = azulResponse.AzulOrderId || null;
    const authorizationCode = azulResponse.AuthorizationCode || null;
    const dateTime = azulResponse.DateTime || new Date().toISOString();

    const formattedDate = new Date(dateTime).toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const formattedAmount = amount.toLocaleString('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 2
    });

    return {
      tipId: parseInt(tipIdFromStorage, 10),
      amount: formattedAmount,
      employeeName: employee.name,
      date: formattedDate,
      azulOrderId,
      authorizationCode,
      headerColor: employee.headerLandingColor || "#bc4c9d",
      textColor: employee.textLandingColor || "#575b5b",
    };
  };

  const tipSummary = getTipSummaryData();
  const showTipSummary = !isLoading && employee && isFromAzul && tipSummary;
  const showDownloadButton = !isLoading && employee && isFromAzul && showTipSummary;
  const showGoogleReviewButton = !isLoading && employee && isFromAzul && googleRedirectUrl;

  return (
    <ConfirmationPageBase
      employee={employee}
      isLoading={isLoading}
      error={error}
      title={title}
      message={message}
    >
      <div className="px-4 sm:px-6 pb-6 space-y-4">
        {showTipSummary && (
          <div className="w-full max-w-sm mx-auto">
            <div className="flex flex-col gap-3">
              <div className="flex items-end">
                <span className="text-[#a6a6a6] w-[5.5rem] shrink-0 text-right">Subtotal</span>
                <div className="flex flex-col items-end w-48 shrink-0">
                  <span className="text-[#a6a6a6] w-full mx-auto">{tipSummary.amount}</span>
                  <div className="h-[1px] w-full bg-[#e0e0e0]" />
                </div>
              </div>
              <div className="flex items-end">
                <span className="text-[#a6a6a6] w-[5.5rem] shrink-0 text-right">Impuestos</span>
                <div className="flex flex-col items-end w-48 shrink-0">
                  <span className="text-[#a6a6a6] w-full mx-auto">$0</span>
                  <div className="h-[1px] w-full bg-[#e0e0e0]" />
                </div>
              </div>
              <div className="flex items-end">
                <span className="text-[#a6a6a6] w-[5.5rem] shrink-0 text-right">Total</span>
                <div className="flex flex-col items-end w-48 shrink-0">
                  <span className="text-[#a6a6a6] w-full mx-auto">{tipSummary.amount}</span>
                  <div className="h-[1px] w-full bg-[#e0e0e0]" />
                </div>
              </div>
            </div>
          </div>
        )}

        {showDownloadButton && (
          <div className="flex pt-4 flex-col items-center">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="inline-flex bg-primary text-white items-center justify-center gap-2 whitespace-nowrap rounded-xl md:rounded-2xl text-xs md:text-sm font-medium transition-colors hover:bg-primary/80 px-3 py-1 md:px-5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M12 3V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 10L12 14L16 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 21H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{downloading ? "Generando comprobante..." : "Descargar comprobante de pago"}</span>
            </button>
            {pdfError && (
              <div className="mt-2 text-sm text-red-600 text-center">
                {pdfError}
              </div>
            )}
          </div>
        )}

        {showGoogleReviewButton && (
          <div className="border-t border-[#e0e0e0] pt-4 mt-4">
            <h2 className="text-[#3a3a3a] text-lg sm:text-xl mb-3 sm:mb-4">
              Deja tu opinión en Google
            </h2>
            {googleSecondsLeft != null && googleSecondsLeft > 0 && (
              <p className="text-sm text-gray-600 mb-4">
                Te redirigiremos automáticamente en <span className="font-semibold text-gray-900">{googleSecondsLeft}s</span>.
              </p>
            )}
            <a
              href={googleRedirectUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center text-black border border-[#4285F4] rounded-full py-2 px-4 sm:px-6 hover:bg-[#4285F4]/10 transition-colors text-sm sm:text-base"
            >
              <svg
                className="w-5 h-5 mr-2"
                viewBox="-0.5 0 48 48"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g fill="none" fillRule="evenodd">
                  <path
                    d="M9.827 24c0-1.524.253-2.986.705-4.356l-7.909-6.04A23.456 23.456 0 0 0 .213 24c0 3.737.868 7.26 2.407 10.388l7.905-6.05A13.885 13.885 0 0 1 9.827 24"
                    fill="#FBBC05"
                  />
                  <path
                    d="M23.714 10.133c3.311 0 6.302 1.174 8.652 3.094L39.202 6.4C35.036 2.773 29.695.533 23.714.533a23.43 23.43 0 0 0-21.09 13.071l7.908 6.04a13.849 13.849 0 0 1 13.182-9.51"
                    fill="#EB4335"
                  />
                  <path
                    d="M23.714 37.867a13.849 13.849 0 0 1-13.182-9.51l-7.909 6.038a23.43 23.43 0 0 0 21.09 13.072c5.732 0 11.205-2.036 15.312-5.849l-7.507-5.804c-2.118 1.335-4.786 2.053-7.804 2.053"
                    fill="#34A853"
                  />
                  <path
                    d="M46.145 24c0-1.387-.213-2.88-.534-4.267H23.714V28.8h12.604c-.63 3.091-2.346 5.468-4.8 7.014l7.507 5.804c4.314-4.004 7.12-9.969 7.12-17.618"
                    fill="#4285F4"
                  />
                </g>
              </svg>
              Valorar en Google
            </a>
          </div>
        )}
      </div>
    </ConfirmationPageBase>
  );
}

